import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { useCurrentJob, useCurrentJobLead } from 'hooks';
import usePreviousValue from 'hooks/usePreviousValue';
import { FIELD_NAMES } from 'pages/Quote/constants';
import { getDepositLimit } from 'pages/Quote/helpers';

import { CheckoutFinanceRate } from '../constants';

export type OutOfBoundsDepositHook = void;

export default function useOutOfBoundsDeposit(
  rates: CheckoutFinanceRate[] = [],
): OutOfBoundsDepositHook {
  const { job } = useCurrentJob();
  const { lead } = useCurrentJobLead();

  const { setValue } = useFormContext();

  const [duration, deposit, interest, loanAmount] = useWatch({
    name: [
      FIELD_NAMES.duration,
      FIELD_NAMES.depositAmount,
      FIELD_NAMES.interest,
      FIELD_NAMES.loanAmount,
    ],
  });

  const depositAmount = useMemo(() => parseInt(deposit, 10), [deposit]);

  const [selectedRate] = useMemo(
    () =>
      rates.filter((rate: CheckoutFinanceRate) => rate.duration === duration) ||
      [],
    [rates, duration],
  );

  const depositLimits = useMemo(
    () => getDepositLimit(selectedRate?.name, job, lead),
    [selectedRate?.name, job, lead],
  );

  const previousSelectedRate = usePreviousValue(selectedRate);

  const jobTotal = job?.total?.amount_vat_inclusive?.amount || 0;

  // Effect that will update the deposit, if the duration changes
  // and the current deposit value is out of bounds of the new limits
  useEffect(() => {
    if (selectedRate) {
      const minDeposit = parseInt(depositLimits.min, 10);
      const maxDeposit = parseInt(depositLimits.max, 10);

      // Slider variables
      const min = jobTotal - maxDeposit;
      const max = jobTotal - minDeposit;

      if (loanAmount < min || loanAmount > max) {
        // When out of bounds, reset to the current min deposit
        setValue(FIELD_NAMES.depositAmount, String(minDeposit));
        setValue(FIELD_NAMES.loanAmount, max);
      }
    }
  }, [
    interest,
    selectedRate,
    depositAmount,
    depositLimits,
    previousSelectedRate,
    job,
    jobTotal,
    loanAmount,
    lead,
    setValue,
  ]);
}
